import { SkyButton, SkyCard, SkyText } from "@skypointcloud/skypoint-ui";
import { useState, type ReactNode } from "react";
import { useEmailDelivery } from "@/hooks/use-notifications";
import type { EmailDeliveryView } from "@shared/types/notifications";
import {
  RUNNER_NOT_REPORTED,
  SENDER_NOT_CONFIGURED,
  UNKNOWN,
  keyCheckSentence,
  last24hSentence,
  lastFailureSentence,
} from "@/lib/notifications/sentences";
import { ErrorState } from "./ScreenState";
import { SendTestEmailDialog } from "./SendTestEmailDialog";
import { formatInstant } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";

// copy-jargon-file-ok: an operator setting: how the runner reads the email key. (#929, scripts/check-copy.mjs)

/**
 * Platform settings > General > Email delivery (contract-notif R9, D2; round-1 ruling 15).
 *
 * Read-only, from the runner-written status row and the last 24 hours of deliveries only: the
 * web never reads the runner's health route or its environment, and never the key. The key row
 * says "SendGrid API key" and shows the vault and the secret the runner reads, never the value,
 * never a fingerprint and never the name of the variable that points at it (round-2 critic
 * ruling 5). "Send a test email" proves the key end to end.
 */
function Row({ label, testId, children }: { label: string; testId: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-1 py-1 sm:grid-cols-[12rem_1fr] sm:gap-4">
      <SkyText size={300} weight="medium">
        {label}
      </SkyText>
      <SkyText size={300} block testId={testId}>
        {children}
      </SkyText>
    </div>
  );
}

/** The card's rows once the status has loaded. */
function EmailDeliveryRows({ view, zone }: { view: EmailDeliveryView; zone: string }) {
  return (
    <div className="flex flex-col">
      {!view.reported ? (
        <SkyText size={300} block testId="email-not-reported">
          {RUNNER_NOT_REPORTED}
        </SkyText>
      ) : null}
      <Row label="SendGrid API key" testId="email-key-pointer">
        {view.vaultName && view.secretName
          ? `${view.vaultName} / ${view.secretName}`
          : view.reported
            ? "Not configured"
            : UNKNOWN}
      </Row>
      {view.reported ? (
        <Row label="Key check" testId="email-key-check">
          {keyCheckSentence(view, zone)}
        </Row>
      ) : null}
      <Row label="Verified sender" testId="email-sender">
        {view.sender ?? (view.reported ? SENDER_NOT_CONFIGURED : UNKNOWN)}
      </Row>
      <Row label="Last successful send" testId="email-last-success">
        {view.lastSuccessAt
          ? formatInstant(view.lastSuccessAt, zone, "short", { named: true })
          : view.reported
            ? "Never"
            : UNKNOWN}
      </Row>
      <Row label="Last failure" testId="email-last-failure">
        {view.reported ? lastFailureSentence(view, zone) : UNKNOWN}
      </Row>
      <Row label="Last 24 hours" testId="email-last-24h">
        {last24hSentence(view.last24h)}
      </Row>
    </div>
  );
}

export function EmailDeliveryCard() {
  const zone = useDisplayTz();
  const email = useEmailDelivery();
  const [dialogOpen, setDialogOpen] = useState(false);
  const view = email.data;

  function renderEmail(): ReactNode {
    if (email.isPending) {
      return (
        // A line, not the library skeleton: its `aria-label` on a role-less div is an axe
        // finding (aria-prohibited-attr), and this card loads while the page is already shown.
        <SkyText size={200} color="muted" block role="status" testId="email-delivery-loading">
          Loading email delivery…
        </SkyText>
      );
    }
    if (email.isError) {
      return (
        <ErrorState
          title="Email delivery did not load"
          error={email.error}
          onRetry={() => void email.refetch()}
          testId="email-delivery-error"
        />
      );
    }
    if (view) {
      return <EmailDeliveryRows view={view} zone={zone} />;
    }
    return null;
  }

  return (
    <SkyCard appearance="outline" testId="email-delivery-card">
      <div className="flex flex-col gap-3 p-1">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <SkyText as="h2" size={400} weight="semibold" block>
              Email delivery
            </SkyText>
            <SkyText size={200} color="muted" block>
              The runner reads the SendGrid API key from Key Vault when it starts and every 15
              minutes, and reports here. The key itself is never shown.
            </SkyText>
          </div>
          {view ? (
            <SkyButton
              appearance="secondary"
              testId="send-test-email"
              onClick={() => setDialogOpen(true)}
            >
              Send a test email
            </SkyButton>
          ) : null}
        </div>

        {renderEmail()}
      </div>

      {view ? (
        <SendTestEmailDialog
          // Remounted per opening, so a result from the last test is not shown for the next.
          key={`send-test-email-${dialogOpen}`}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          view={view}
        />
      ) : null}
    </SkyCard>
  );
}
